import { useRef, useState, useCallback } from "react";

// Interactive before/after comparison slider. Drag the handle (or use the
// range input for keyboard/touch) to wipe between the two photos.
export default function BeforeAfter({ before, after, alt = "Project", testid }) {
  const [pos, setPos] = useState(50);
  const containerRef = useRef(null);
  const dragging = useRef(false);

  const setFromClientX = useCallback((clientX) => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const pct = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.max(0, Math.min(100, pct)));
  }, []);

  const onPointerDown = (e) => {
    dragging.current = true;
    setFromClientX(e.clientX);
  };
  const onPointerMove = (e) => {
    if (!dragging.current) return;
    setFromClientX(e.clientX);
  };
  const stop = () => { dragging.current = false; };

  return (
    <div
      ref={containerRef}
      data-testid={testid}
      className="relative w-full aspect-[4/3] overflow-hidden rounded-sm select-none cursor-ew-resize bg-charcoal/5 group"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={stop}
      onPointerLeave={stop}
    >
      {/* After (full background) */}
      <img
        src={after}
        alt={`${alt} — after`}
        draggable={false}
        className="absolute inset-0 w-full h-full object-cover"
      />
      <span className="absolute bottom-3 right-3 z-10 text-xs font-700 tracking-wide uppercase bg-emerald text-white px-2.5 py-1 rounded-sm">
        After
      </span>

      {/* Before (clipped overlay) */}
      <div
        className="absolute inset-0 overflow-hidden"
        style={{ width: `${pos}%` }}
      >
        <img
          src={before}
          alt={`${alt} — before`}
          draggable={false}
          style={{ width: containerRef.current ? containerRef.current.offsetWidth : "100%" }}
          className="absolute inset-0 h-full max-w-none object-cover"
        />
        <span className="absolute bottom-3 left-3 text-xs font-700 tracking-wide uppercase bg-charcoal/85 text-white px-2.5 py-1 rounded-sm">
          Before
        </span>
      </div>

      {/* Handle */}
      <div
        className="absolute top-0 bottom-0 z-20 w-0.5 bg-white shadow-[0_0_0_1px_rgba(0,0,0,0.15)]"
        style={{ left: `${pos}%`, transform: "translateX(-50%)" }}
      >
        <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 grid place-items-center w-9 h-9 rounded-full bg-white shadow-lg text-charcoal">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
            <polyline points="9 18 3 12 9 6" transform="translate(12 0)" />
          </svg>
        </span>
      </div>
    </div>
  );
}
